import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  DownloadOutlined,
  RefreshOutlined,
  SmartToyOutlined,
} from "@mui/icons-material";
import CommonDialog from "../../common/CommonDialog";
import { mediaSrc } from "../../utils/mediaSrc";

function safeParseJson(s) {
  try {
    return JSON.parse(s || "{}");
  } catch {
    return {};
  }
}

export default function VideoAgentJobDetailModal({
  item,
  open,
  onClose,
  lang,
  hitAxios,
  showSnack,
  onRefreshList,
}) {
  const theme = useTheme();
  const videoRef = React.useRef(null);
  const [live, setLive] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [polling, setPolling] = React.useState(false);

  const row = live || item;
  const id = row?.id;

  React.useEffect(() => {
    if (!open || !id) {
      setLive(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await hitAxios({
        path: `/api/heygen/video-agent/jobs/${id}`,
        post: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (!cancelled && res?.data?.success && res.data.data?.job) {
        setLive(res.data.data.job);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, id, hitAxios]);

  if (!row) return null;

  const opts = safeParseJson(row.options_json);
  const videoUrl = row.generated_video ? mediaSrc(row.generated_video) : null;
  const st = String(row.status || "").toLowerCase();
  const isProcessing = st === "processing" || st === "pending";
  const isFailed = st === "failed" || st === "error";
  const isComplete = st === "completed" && row.generated_video;

  const handleDownload = () => {
    if (!videoUrl) return;
    window.open(videoUrl, "_blank", "noopener,noreferrer");
  };

  const runPoll = async () => {
    if (!id) return;
    setPolling(true);
    try {
      const res = await hitAxios({
        path: `/api/heygen/video-agent/jobs/${id}/poll`,
        post: true,
        obj: {},
        showLoading: false,
        showSnackbar: false,
      });
      if (res?.data?.success && res.data.data?.job) {
        setLive(res.data.data.job);
        onRefreshList?.();
        if (res.data.data.finalized && res.data.data.job?.status === "completed") {
          showSnack(lang?.videoAgentVideoReady || "Your video is ready.", true);
        }
        if (res.data.data.job?.status === "failed") {
          showSnack(lang?.videoAgentVideoFailed || "Video generation failed.", false);
        }
      } else {
        showSnack(res?.data?.msg || lang?.videoAgentGenerateError || "Request failed.", false);
      }
    } finally {
      setPolling(false);
    }
  };

  const title = String(row.title || row.prompt || "").trim() || lang?.videoAgentUntitled || "Video Agent";

  return (
    <CommonDialog
      open={open}
      onClose={onClose}
      title={title}
      icon={SmartToyOutlined}
      maxWidth="sm"
      fullWidth
      contentSx={{ pt: 1 }}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            {lang?.pleaseWait || "Please wait…"}
          </Typography>
        ) : null}

        {videoUrl ? (
          <Box
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "grey.900",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box
              component="video"
              ref={videoRef}
              src={videoUrl}
              controls
              playsInline
              style={{ width: "100%", display: "block", maxHeight: 380 }}
            />
            <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.background.paper, 0.96) }}>
              <Button
                variant="contained"
                onClick={handleDownload}
                startIcon={<DownloadOutlined />}
                sx={{ fontWeight: 700, textTransform: "none" }}
              >
                {lang?.download || "Download"}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              py: 6,
              textAlign: "center",
              borderRadius: 2,
              border: `1px dashed ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.text.primary, 0.03),
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {isFailed
                ? String(row.error_message || lang?.videoAgentVideoFailed || "Generation failed.")
                : lang?.videoAgentNoVideoYet ||
                  "Video file is not stored yet. When HeyGen finishes, use Check status to pull it into your library."}
            </Typography>
            {isProcessing || (!isFailed && !isComplete) ? (
              <Button
                variant="outlined"
                startIcon={<RefreshOutlined />}
                onClick={runPoll}
                disabled={polling}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                {lang?.videoAgentCheckStatus || "Check status"}
              </Button>
            ) : null}
          </Box>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`${lang?.videoAgentJobStatus || "Status"}: ${row.status || "—"}`} />
          {row.heygen_video_id ? (
            <Chip size="small" variant="outlined" label={`HeyGen: ${String(row.heygen_video_id).slice(0, 18)}…`} />
          ) : null}
        </Stack>

        <Divider />

        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {lang?.prompt || "Prompt"}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
          {row.prompt || "—"}
        </Typography>

        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {lang?.videoAgentDetailOptions || "Options"}
        </Typography>
        <Typography variant="body2" component="pre" sx={{ fontFamily: "inherit", m: 0, fontSize: 13 }}>
          {JSON.stringify(opts, null, 2)}
        </Typography>
      </Stack>
    </CommonDialog>
  );
}
